import { Add } from "@mui/icons-material"
import { Button, Collapse, Grid2 as Grid, IconButton, Stack, Tooltip } from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import React, { useEffect, useState } from "react"
import { useDeleteProjectAoi, useFetchProjectAoi, useFetchProjectFullData } from "../../api/ProjectsApi"
import { QueryKeys } from "../../api/QueryConfig"
import { NexAutocomplete } from "../../components/Fields/Autocomplete"
import { LoadingButton } from "../../components/LoadingButton/LoadingButton"
import { AlertModal } from "../../components/Modals/AlertModal"
import { Label } from "../../components/Typography/Typography"
import { useNexusNavigate } from "../../hooks/useNexusNavigate"
import { useNexusAoiId, useNexusProjectNumber } from "../../hooks/useNexusParams"
import { AoiName } from "../../models/aois/AoiName"
import { useAcquisitionModeStore } from "../../zstore/AcquisitionModeStore"
import { AoiEditMode, useProjectEditStore } from "../../zstore/ProjectEditStore"
import { useProjectEditorStyles } from "./ProjectEditorStyles"

/**
 * Fetch the necessary data so we can give concrete props to the inner component.
 * @constructor
 */
export const AOISelect = () => {
    //Simple Hooks
    const { classes } = useProjectEditorStyles()
    const projectNumber = useNexusProjectNumber()
    const aoiId = useNexusAoiId()
    const queryClient = useQueryClient()
    const nexusNav = useNexusNavigate()

    // State Management Hooks
    const [deleteOpen, setDeleteOpen] = useState(false)
    const mode = useProjectEditStore((state) => state.aoiEditMode)
    const setMode = useProjectEditStore((state) => state.setAoiEditMode)
    const submitNewAoi = useProjectEditStore((state) => state.submitNewAoi)
    const setDeletingAoi = useProjectEditStore((state) => state.setDeletingAoi)
    const selectedAoi = useProjectEditStore((state) => state.selectedAoi)
    const setSelectedAoi = useProjectEditStore((state) => state.setSelectedAoi)
    const clearSelected = useAcquisitionModeStore((state) => state.clearSelected)
    const setError = useProjectEditStore((state) => state.setError)
    const setSelectedModesForAoi = useAcquisitionModeStore((state) => state.setSelectedModesForAoi)

    // Tanstack Query Hooks
    const fetchedAoi = useFetchProjectAoi(aoiId)
    const deleteAoi = useDeleteProjectAoi()
    const fetchedProjectData = useFetchProjectFullData(projectNumber)

    // Derived Component State
    const aoiList = fetchedProjectData.data?.aoi_list?.slice() ?? []
    const requestError = fetchedAoi.error?.message ?? deleteAoi.error?.message
    const aoiIsPending = fetchedAoi.isPending && aoiId != null

    let label = "Select an Existing AOI to Edit or Submit a New AOI"
    if (mode === AoiEditMode.SUBMIT) {
        label = "Submitting New AOI"
    } else if (mode === AoiEditMode.EDIT) {
        label = "Editing AOI"
    }

    // Use Effects
    useEffect(() => {
        // Update the selected AOI if needed
        if (fetchedAoi.data == null) {
            // No AOI selected
            clearSelected()
            setMode(AoiEditMode.SELECT)
        } else {
            setSelectedAoi(fetchedAoi.data)
            setSelectedModesForAoi(fetchedAoi.data)
            setMode(AoiEditMode.EDIT)
        }
    }, [fetchedAoi.data?.aoi_id])

    useEffect(() => {
        if (requestError != null) {
            setError(requestError)
        }
    }, [requestError])

    // Handler Functions
    const handleEditSelect = async (value: AoiName) => {
        setMode(AoiEditMode.EDIT)
        clearSelected()
        nexusNav.editProject(projectNumber!, { aoiId: value.aoi_id })
    }

    const handleSubmitSelect = (event: React.MouseEvent) => {
        clearSelected()
        event.stopPropagation()
        setMode(AoiEditMode.SUBMIT)
        submitNewAoi()
    }

    const handleDelete = async () => {
        setDeleteOpen(false)
        setDeletingAoi(true)
        if (aoiId == null) {
            throw new Error("Aoi id must be present to delete an AOI.")
        }
        await deleteAoi.mutateAsync(aoiId)
        clearSelected()
        setSelectedAoi(undefined)

        // Refetch the project with its AOIs
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.FIND_PROJECT, projectNumber] })
        setDeletingAoi(false)
    }

    const handleCancel = () => {
        setMode(AoiEditMode.SELECT)
        clearSelected()
        setSelectedAoi(undefined)
        nexusNav.editProject(projectNumber!)
    }

    return (
        <Stack>
            <Label>{label}</Label>
            <Collapse in={mode !== AoiEditMode.SUBMIT}>
                <NexAutocomplete
                    TextFieldProps={{
                        label: "Edit Existing AOI",
                        helperText: selectedAoi != null ? "Switching the selected AOI will lose any new changes" : "",
                    }}
                    width="md"
                    disableClearable={selectedAoi != null}
                    disabled={aoiIsPending || deleteAoi.isPending || fetchedProjectData.isPending}
                    loading={aoiIsPending || fetchedProjectData.isPending}
                    value={aoiList.find((aoi) => aoi.name === selectedAoi?.name) ?? null}
                    options={aoiList.sort((a, b) => (a.name > b.name ? 1 : -1))}
                    getOptionLabel={(option: AoiName) => option.name || ""}
                    onChange={async (_, value) => {
                        if (value != null) {
                            await handleEditSelect(value)
                        }
                    }}
                    sx={{ mb: 2 }}
                    endAdornment={
                        mode === AoiEditMode.SELECT &&
                        !aoiIsPending && (
                            <Tooltip title="Submit New AOI">
                                <IconButton size="small" onClick={handleSubmitSelect}>
                                    <Add />
                                </IconButton>
                            </Tooltip>
                        )
                    }
                />
            </Collapse>
            <Collapse in={mode !== AoiEditMode.SELECT}>
                <Grid container spacing={2}>
                    {mode === AoiEditMode.EDIT && (
                        <Grid>
                            <LoadingButton
                                pending={deleteAoi.isPending}
                                disabled={aoiIsPending || aoiId == null}
                                variant="outlined"
                                onClick={() => setDeleteOpen(true)}
                                className={classes.btn_delete}
                            >
                                Delete
                            </LoadingButton>
                        </Grid>
                    )}
                    <Grid>
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={deleteAoi.isPending || aoiIsPending}
                            onClick={handleCancel}
                        >
                            {mode === AoiEditMode.SUBMIT ? "Cancel Submission" : "Cancel Edit"}
                        </Button>
                    </Grid>
                </Grid>
            </Collapse>
            <AlertModal
                danger
                open={deleteOpen}
                pending={deleteAoi.isPending}
                title="Delete AOI?"
                onConfirm={handleDelete}
                onClose={() => setDeleteOpen(false)}
            >
                Are you sure you want to delete this AOI? This action cannot be reversed.
            </AlertModal>
        </Stack>
    )
}
